import React, { memo, useState, useId } from 'react';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'multiline',
})(({ multiline, theme }) => ({
  width: '100%',
  background: 'var(--color-surface)',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  outline: 'none',
  height: multiline ? 'auto' : '40px',
  padding: multiline ? '8px 0' : 0,
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledInput = styled(InputBase)(({ multiline, theme }) => ({
  width: '100%',
  height: multiline ? 'auto' : '40px',
  paddingLeft: '8px',
  paddingRight: '8px',

  '& input, & textarea': {
    fontSize: 14,
    color: theme.palette.text.primary,
    resize: 'none',
  },
  '& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-inputMultiline.Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.secondary,
    opacity: 0.7,
  },
}));

const Input = (props) => {
  const {
    value = '',
    disabled = false,
    placeholder = '',
    onChange = () => {},
    type = 'text',
    label = '',
    required,
    multiline = false,
    minRows = 3,
    maxRows,
    autoFocus = false,
    sx,
  } = props;

  const id = useId();
  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);

  const inputType =
    isPassword ? (showPassword ? 'text' : 'password') : type;

  return (
    <>
      {!!label && (
        <Box sx={{ display: 'flex', marginBottom: '2px' }}>
          <Typography sx={{ fontSize: '14px' }}>
            {label}
          </Typography>
          {required && (
            <Typography sx={(theme) => ({ fontSize: '16px', ml: '1px', color: theme.palette.primary.main })}>
              *
            </Typography>
          )}
        </Box>
      )}

      <StyledFormControl multiline={multiline} sx={sx}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <StyledInput
            id={`input-${id}`}
            type={inputType}
            multiline={multiline}
            minRows={multiline ? minRows : undefined}
            maxRows={multiline ? maxRows : undefined}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            sx={{
              paddingRight: isPassword ? '40px' : '8px',
              '& textarea::placeholder, & input::placeholder': {
                color: 'var(--color-text-muted)',
                opacity: 1,
              },
            }}
            autoFocus={autoFocus}
          />

          {isPassword && !multiline && (
            <IconButton
              size="small"
              onClick={() => setShowPassword((prev) => !prev)}
              sx={{
                position: 'absolute',
                right: 6,
                color: 'var(--color-text-muted)',
              }}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          )}
        </Box>
      </StyledFormControl>
    </>
  );
};

export default memo(Input);
